import React, { Component } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from "@material-ui/core";
import { withAlert } from "react-alert";
import styled from "styled-components";
import moment from "moment";
import "moment/locale/pt-br";
import { withTranslation } from "react-i18next";
import Button from "../../../waybee-ui/Button";
import Group from "../../../waybee-ui/Group";
import JobsService from "../../../services/JobsService";
import fetchGradeJobQueue from "../../../utils/fetchGradeJobQueue";

const ERROR_PREVIEW_LENGTH = 180;
const INCREMENTAL_MODE_FIXED = "fixed";
const INCREMENTAL_MODE_INTERVAL = "interval";

const WarningText = styled(Typography)`
  && {
    color: #a66a00;
    background: #fff8e6;
    border: 1px solid #f0d9a0;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 20px;
    font-size: 14px;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 24px;
`;

const StatusCard = styled.div`
  padding: 14px 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fafafa;
`;

const InfoLabel = styled(Typography)`
  && {
    font-size: 12px;
    color: #888;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    margin-bottom: 4px;
  }
`;

const InfoValue = styled(Typography)`
  && {
    font-size: 16px;
    color: #333;
    font-weight: 500;
  }
`;

const ErrorCard = styled(StatusCard)`
  grid-column: 1 / -1;
`;

const ErrorValue = styled(Typography)`
  && {
    font-size: 14px;
    color: #333;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const MoreLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: ${({ theme }) => theme.color.primary.main};
  cursor: pointer;
  font-size: 13px;
  text-align: left;
`;

const ScheduleSection = styled.div`
  margin-bottom: 28px;
  border: 1px solid #e4e4e4;
  border-radius: 10px;
  background: #fff;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 16px 18px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 18px;
    color: #333;
  }
`;

const SectionHint = styled(Typography)`
  && {
    color: #777;
    margin-top: 4px;
    font-size: 14px;
  }
`;

const ScheduleBody = styled.div`
  padding: 16px 18px 0;
`;

const ModeGroup = styled(RadioGroup)`
  && {
    margin-bottom: 16px;
  }
`;

const ScheduleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
`;

const ScheduleCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
  border: 1px solid #eee;
  border-radius: 8px;
  background: #fafafa;
`;

const SlotTitle = styled(Typography)`
  && {
    font-size: 15px;
    font-weight: 600;
    color: #2f2f2f;
  }
`;

const ScheduleFields = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 108px;
  gap: 12px;
`;

const IntervalFields = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr) minmax(0, 1fr);
  gap: 12px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const HoursChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 24px;
`;

const ScheduleFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
  padding: 14px 18px;
  border-top: 1px solid #f0f0f0;
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const emptyScheduleForm = {
  incrementalMode: INCREMENTAL_MODE_FIXED,
  incrementalHours: "",
  incrementalMinute: "0",
  everyMinutes: "30",
  windowStart: "07:00",
  windowEnd: "22:00",
  fullHours: "",
  fullMinute: "0"
};

const hoursToInput = slot =>
  slot && Array.isArray(slot.hour) ? slot.hour.join(", ") : "";

const minuteToInput = slot =>
  slot && slot.minute != null ? String(slot.minute) : "0";

const parseHoursInput = value =>
  String(value || "")
    .split(/[,\s]+/)
    .map(item => Number(item))
    .filter(n => Number.isInteger(n) && n >= 0 && n <= 23);

const parseMinuteInput = value => {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0 || n > 59) {
    return null;
  }
  return n;
};

const parseEveryMinutes = value => {
  const n = Number(value);
  if (!Number.isInteger(n) || n < 1 || n > 1440) {
    return null;
  }
  return n;
};

const parseWindowTime = value => {
  const match = String(value || "")
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return null;
  }
  if (!Number.isInteger(minute) || minute < 0 || minute > 59) {
    return null;
  }
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const formatTimeChip = (hour, minute) => {
  const hh = String(hour).padStart(2, "0");
  const mm = String(minute == null ? 0 : minute).padStart(2, "0");
  return `${hh}:${mm}`;
};

const isIntervalSlot = slot =>
  !!(
    slot &&
    (slot.mode === INCREMENTAL_MODE_INTERVAL ||
      (slot.everyMinutes != null && slot.windowStart && slot.windowEnd))
  );

const scheduleToForm = schedule => {
  if (!schedule) {
    return emptyScheduleForm;
  }
  const { incremental } = schedule;
  if (isIntervalSlot(incremental)) {
    return {
      ...emptyScheduleForm,
      incrementalMode: INCREMENTAL_MODE_INTERVAL,
      everyMinutes:
        incremental.everyMinutes != null
          ? String(incremental.everyMinutes)
          : emptyScheduleForm.everyMinutes,
      windowStart: parseWindowTime(incremental.windowStart) || "07:00",
      windowEnd: parseWindowTime(incremental.windowEnd) || "22:00",
      fullHours: hoursToInput(schedule.full),
      fullMinute: minuteToInput(schedule.full)
    };
  }
  return {
    ...emptyScheduleForm,
    incrementalMode: INCREMENTAL_MODE_FIXED,
    incrementalHours: hoursToInput(incremental),
    incrementalMinute: minuteToInput(incremental),
    fullHours: hoursToInput(schedule.full),
    fullMinute: minuteToInput(schedule.full)
  };
};

class LbSync extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      submitting: false,
      savingSchedule: false,
      status: null,
      errorExpanded: false,
      scheduleForm: emptyScheduleForm
    };
  }

  componentDidMount() {
    this.loadStatus();
  }

  loadStatus = async () => {
    const { alert, t, i18n } = this.props;
    moment.locale(i18n.language === "en" ? "en" : "pt-br");
    this.setState({ loading: true, errorExpanded: false });
    try {
      const status = await JobsService.checkSyncLb();
      this.setState({
        status,
        loading: false,
        scheduleForm: scheduleToForm(status && status.schedule)
      });
    } catch (error) {
      this.setState({ loading: false });
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message) ||
        t("settings.lbSync.loadError");
      alert.error(message);
    }
  };

  handleSync = async (forceFull = false) => {
    const { alert, t } = this.props;
    this.setState({ submitting: true });
    try {
      await JobsService.syncLb(forceFull);
      await fetchGradeJobQueue();
      alert.success(t("settings.lbSync.queued"));
      await this.loadStatus();
    } catch (error) {
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message) ||
        t("settings.lbSync.startError");
      alert.error(message);
    } finally {
      this.setState({ submitting: false });
    }
  };

  handleScheduleField = field => event => {
    const { value } = event.target;
    this.setState(prev => ({
      scheduleForm: {
        ...prev.scheduleForm,
        [field]: value
      }
    }));
  };

  handleIncrementalMode = event => {
    const incrementalMode = event.target.value;
    this.setState(prev => ({
      scheduleForm: {
        ...prev.scheduleForm,
        incrementalMode
      }
    }));
  };

  buildIncrementalPayload = () => {
    const { alert, t } = this.props;
    const { scheduleForm, status } = this.state;
    const isIncremental =
      status && status.syncStrategy === "incremental-stable";
    const useInterval =
      isIncremental &&
      scheduleForm.incrementalMode === INCREMENTAL_MODE_INTERVAL;

    if (useInterval) {
      const everyMinutes = parseEveryMinutes(scheduleForm.everyMinutes);
      const windowStart = parseWindowTime(scheduleForm.windowStart);
      const windowEnd = parseWindowTime(scheduleForm.windowEnd);
      if (!everyMinutes || !windowStart || !windowEnd) {
        alert.error(t("settings.lbSync.scheduleIntervalError"));
        return null;
      }
      return {
        mode: INCREMENTAL_MODE_INTERVAL,
        everyMinutes,
        windowStart,
        windowEnd
      };
    }

    const incrementalHours = parseHoursInput(scheduleForm.incrementalHours);
    const incrementalMinute = parseMinuteInput(scheduleForm.incrementalMinute);
    if (!incrementalHours.length || incrementalMinute == null) {
      alert.error(t("settings.lbSync.scheduleSaveError"));
      return null;
    }
    return {
      hour: incrementalHours,
      minute: incrementalMinute
    };
  };

  handleSaveSchedule = async () => {
    const { alert, t } = this.props;
    const { scheduleForm, status } = this.state;
    const isIncremental =
      status && status.syncStrategy === "incremental-stable";
    const incremental = this.buildIncrementalPayload();
    if (!incremental) {
      return;
    }

    const payload = { incremental };

    if (isIncremental) {
      const fullHours = parseHoursInput(scheduleForm.fullHours);
      const fullMinute = parseMinuteInput(scheduleForm.fullMinute);
      if (fullHours.length) {
        if (fullMinute == null) {
          alert.error(t("settings.lbSync.scheduleSaveError"));
          return;
        }
        payload.full = { hour: fullHours, minute: fullMinute };
      }
    }

    this.setState({ savingSchedule: true });
    try {
      const schedule = await JobsService.saveLbSyncSchedule(payload);
      this.setState(prev => ({
        savingSchedule: false,
        status: { ...prev.status, schedule },
        scheduleForm: scheduleToForm(schedule)
      }));
      alert.success(t("settings.lbSync.scheduleSaved"));
    } catch (error) {
      this.setState({ savingSchedule: false });
      const message =
        (error.response &&
          error.response.data &&
          error.response.data.error &&
          error.response.data.error.message) ||
        t("settings.lbSync.scheduleSaveError");
      alert.error(message);
    }
  };

  formatSyncDate = lastSync => {
    const { t } = this.props;
    if (!lastSync || !lastSync.lastRunAt)
      return t("settings.lbSync.neverSynced");
    return moment(lastSync.lastRunAt).format("DD/MM/YYYY HH:mm:ss");
  };

  getStatusLabel = (inProgress, syncError) => {
    const { t } = this.props;
    if (inProgress) return t("settings.lbSync.inProgress");
    if (syncError) return t("settings.lbSync.lastError");
    return t("settings.lbSync.ready");
  };

  renderError = syncError => {
    const { t } = this.props;
    const { errorExpanded } = this.state;
    const isLong = syncError.length > ERROR_PREVIEW_LENGTH;
    const text =
      isLong && !errorExpanded
        ? `${syncError.slice(0, ERROR_PREVIEW_LENGTH).trim()}…`
        : syncError;

    return (
      <ErrorCard>
        <InfoLabel>{t("settings.lbSync.lastErrorLabel")}</InfoLabel>
        <ErrorValue>{text}</ErrorValue>
        {isLong && (
          <MoreLink
            type="button"
            onClick={() => this.setState({ errorExpanded: !errorExpanded })}
          >
            {errorExpanded
              ? t("settings.lbSync.seeLess")
              : t("settings.lbSync.seeMore")}
          </MoreLink>
        )}
      </ErrorCard>
    );
  };

  renderFixedSlotFields = (hoursField, minuteField) => {
    const { t } = this.props;
    const { scheduleForm, submitting, savingSchedule } = this.state;
    const disabled = submitting || savingSchedule;
    const hours = parseHoursInput(scheduleForm[hoursField]);
    const minute = parseMinuteInput(scheduleForm[minuteField]);

    return (
      <>
        <ScheduleFields>
          <TextField
            variant="outlined"
            size="small"
            label={t("settings.lbSync.scheduleHours")}
            value={scheduleForm[hoursField]}
            onChange={this.handleScheduleField(hoursField)}
            helperText={t("settings.lbSync.scheduleHoursHint")}
            disabled={disabled}
            fullWidth
          />
          <TextField
            variant="outlined"
            size="small"
            label={t("settings.lbSync.scheduleMinute")}
            value={scheduleForm[minuteField]}
            onChange={this.handleScheduleField(minuteField)}
            type="number"
            inputProps={{ min: 0, max: 59 }}
            disabled={disabled}
          />
        </ScheduleFields>
        <HoursChips>
          {hours.map(hour => (
            <Chip
              key={hour}
              size="small"
              color="primary"
              variant="outlined"
              label={formatTimeChip(hour, minute)}
            />
          ))}
        </HoursChips>
      </>
    );
  };

  renderIntervalSlotFields = () => {
    const { t } = this.props;
    const { scheduleForm, submitting, savingSchedule } = this.state;
    const disabled = submitting || savingSchedule;
    const everyMinutes = parseEveryMinutes(scheduleForm.everyMinutes);
    const windowStart = parseWindowTime(scheduleForm.windowStart);
    const windowEnd = parseWindowTime(scheduleForm.windowEnd);

    return (
      <>
        <IntervalFields>
          <TextField
            variant="outlined"
            size="small"
            label={t("settings.lbSync.scheduleEveryMinutes")}
            value={scheduleForm.everyMinutes}
            onChange={this.handleScheduleField("everyMinutes")}
            type="number"
            inputProps={{ min: 1, max: 1440 }}
            disabled={disabled}
          />
          <TextField
            variant="outlined"
            size="small"
            label={t("settings.lbSync.scheduleWindowStart")}
            value={scheduleForm.windowStart}
            onChange={this.handleScheduleField("windowStart")}
            type="time"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            disabled={disabled}
          />
          <TextField
            variant="outlined"
            size="small"
            label={t("settings.lbSync.scheduleWindowEnd")}
            value={scheduleForm.windowEnd}
            onChange={this.handleScheduleField("windowEnd")}
            type="time"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 60 }}
            disabled={disabled}
          />
        </IntervalFields>
        <HoursChips>
          {everyMinutes && windowStart && windowEnd ? (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              label={t("settings.lbSync.scheduleIntervalChip", {
                minutes: everyMinutes,
                start: windowStart,
                end: windowEnd
              })}
            />
          ) : null}
        </HoursChips>
      </>
    );
  };

  renderScheduleSlot = (title, hoursField, minuteField, useInterval) => (
    <ScheduleCard>
      <SlotTitle>{title}</SlotTitle>
      {useInterval
        ? this.renderIntervalSlotFields()
        : this.renderFixedSlotFields(hoursField, minuteField)}
    </ScheduleCard>
  );

  render() {
    const { loading, submitting, savingSchedule, status, scheduleForm } =
      this.state;
    const { t } = this.props;
    const inProgress = !!(status && status.inProgress);
    const syncError = status && status.error;
    const isIncremental =
      status && status.syncStrategy === "incremental-stable";
    const useInterval =
      isIncremental &&
      scheduleForm.incrementalMode === INCREMENTAL_MODE_INTERVAL;

    return (
      <Box>
        <Group>
          {loading ? (
            <CircularProgress />
          ) : (
            <>
              <WarningText>
                {t(
                  isIncremental
                    ? "settings.lbSync.warningIncremental"
                    : "settings.lbSync.warning"
                )}
              </WarningText>

              <StatusGrid>
                <StatusCard>
                  <InfoLabel>{t("common.labels.status")}</InfoLabel>
                  <InfoValue>
                    {this.getStatusLabel(inProgress, syncError)}
                  </InfoValue>
                </StatusCard>
                <StatusCard>
                  <InfoLabel>{t("settings.lbSync.syncMode")}</InfoLabel>
                  <InfoValue>
                    {t(
                      isIncremental
                        ? "settings.lbSync.modeIncremental"
                        : "settings.lbSync.modeRecreate"
                    )}
                  </InfoValue>
                </StatusCard>
                <StatusCard>
                  <InfoLabel>{t("settings.lbSync.lastSync")}</InfoLabel>
                  <InfoValue>
                    {this.formatSyncDate(status && status.lastSync)}
                  </InfoValue>
                </StatusCard>
                {syncError ? this.renderError(syncError) : null}
              </StatusGrid>

              <ScheduleSection>
                <SectionHeader>
                  <SectionTitle>
                    {t("settings.lbSync.scheduleTitle")}
                  </SectionTitle>
                  <SectionHint>
                    {t(
                      useInterval
                        ? "settings.lbSync.scheduleIntervalHint"
                        : "settings.lbSync.scheduleHint"
                    )}
                  </SectionHint>
                </SectionHeader>
                <ScheduleBody>
                  {isIncremental && (
                    <ModeGroup
                      row
                      value={scheduleForm.incrementalMode}
                      onChange={this.handleIncrementalMode}
                    >
                      <FormControlLabel
                        value={INCREMENTAL_MODE_FIXED}
                        control={<Radio color="primary" />}
                        label={t("settings.lbSync.scheduleModeFixed")}
                        disabled={submitting || savingSchedule}
                      />
                      <FormControlLabel
                        value={INCREMENTAL_MODE_INTERVAL}
                        control={<Radio color="primary" />}
                        label={t("settings.lbSync.scheduleModeInterval")}
                        disabled={submitting || savingSchedule}
                      />
                    </ModeGroup>
                  )}
                  <ScheduleGrid>
                    {this.renderScheduleSlot(
                      isIncremental
                        ? t("settings.lbSync.scheduleIncremental")
                        : t("settings.lbSync.scheduleLegacy"),
                      "incrementalHours",
                      "incrementalMinute",
                      useInterval
                    )}
                    {isIncremental &&
                      this.renderScheduleSlot(
                        t("settings.lbSync.scheduleFull"),
                        "fullHours",
                        "fullMinute",
                        false
                      )}
                  </ScheduleGrid>
                </ScheduleBody>
                <ScheduleFooter>
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={submitting || savingSchedule}
                    onClick={this.handleSaveSchedule}
                  >
                    {t("settings.lbSync.scheduleSave")}
                  </Button>
                </ScheduleFooter>
              </ScheduleSection>

              <Actions>
                <Button
                  color="primary"
                  variant="contained"
                  disabled={submitting || inProgress}
                  onClick={() => this.handleSync(false)}
                >
                  {submitting || inProgress
                    ? t("settings.lbSync.syncing")
                    : t("settings.lbSync.now")}
                </Button>
                {isIncremental && (
                  <Button
                    color="secondary"
                    variant="contained"
                    disabled={submitting || inProgress}
                    onClick={() => this.handleSync(true)}
                  >
                    {t("settings.lbSync.forceFull")}
                  </Button>
                )}
                <Button
                  color="secondary"
                  disabled={loading || submitting}
                  onClick={this.loadStatus}
                >
                  {t("settings.lbSync.refresh")}
                </Button>
              </Actions>
            </>
          )}
        </Group>
      </Box>
    );
  }
}

LbSync.propTypes = {
  alert: PropTypes.shape({
    error: PropTypes.func.isRequired,
    success: PropTypes.func.isRequired
  }).isRequired,
  t: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    language: PropTypes.string
  }).isRequired
};

export default withAlert()(withTranslation()(LbSync));
